import type { UnitDetailsData } from '@/core/api/models/properties';
import type { AdhaLoanDetails } from '@/core/stores/appStore';

function positiveLoanAmount(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return undefined;
  }
  return value;
}

/**
 * Loan slice written from GET /v1/customers/profile.
 * Profile-details `loan_Amount` wins when it is a positive number.
 */
export function mapCatalogueProfileToLoanDetails(
  details: {
    adha_application_number: string;
    sanctioned_loan_amount?: number;
    project_id: string;
    project_name: string;
    project_location: string;
    created_date: string;
  },
  profileLoanAmount?: number | null,
): AdhaLoanDetails {
  const loanAmount =
    positiveLoanAmount(profileLoanAmount) ??
    positiveLoanAmount(details.sanctioned_loan_amount);

  return {
    adhaApplicationNumber: details.adha_application_number,
    projectId: details.project_id,
    projectName: details.project_name,
    projectLocation: details.project_location,
    createdDate: details.created_date,
    sanctionedLoanAmount: loanAmount ?? 0,
  };
}

export interface AdhaCatalogueStoreUpdate {
  readonly projectDetails: UnitDetailsData;
  readonly loanDetails: AdhaLoanDetails;
}

/**
 * Next catalogue slices from one profile response.
 * Built only from that response (plus the current profile-details loan).
 * Previous project amenities, name, and location are not copied in.
 */
export function buildAdhaCatalogueStoreUpdate(
  details: UnitDetailsData,
  profileLoanAmount?: number | null,
): AdhaCatalogueStoreUpdate {
  const amenities = Array.isArray(details.amenities) ? [...details.amenities] : [];
  const projectDetails: UnitDetailsData = {
    ...details,
    amenities,
  };

  return {
    projectDetails,
    loanDetails: mapCatalogueProfileToLoanDetails(projectDetails, profileLoanAmount),
  };
}
